<template>
    <SubLayout
        :pageTitle="title"
        :pageBreadcrumbs="breadcrumbs"
        :hasBack="false"
    >
  
      <template #layoutContent>
        <ActionsBar :query="query" :handleRangeDate="handleRangeDate" :contacts="contacts" :status="paymentStatus" id="payment-datepicker"/>

        <Card :title="$t('payment.list')" containerClass="mb-5 mt-5">
          <template #cardToolbar>
            <div class="d-flex justify-content-end align-items-center">
              <div>
                <Button v-if="$can(abilities.CREATE_PAYMENT)" type="new" btn-class="btn-sm me-2" btn-text="payment.create" @click="openCreateView" ></Button>
              </div>
            </div>
          </template>
          <template #cardBody>
            <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                     id="payments_datatable">
                <thead class="mb-5">
                <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                  <th class="min-w-100px text-center">{{ $t('payment.label.date') }}</th> 
                  <th class="min-w-100px text-center">{{ $t('payment.label.number') }}</th>
                  <th class="min-w-100px text-center">{{ $t('payment.label.customer') }}</th>
                  <th class="min-w-100px text-center">{{ $t('payment.label.mode') }} </th>
                  <th class="min-w-100px text-center">{{ $t('payment.label.invoiceNumber') }} </th>
                  <th class="min-w-100px text-center">{{ $t('payment.label.status') }} </th>
                  <th class="min-w-100px text-center">{{ $t('payment.label.totalAmount') }}</th>
                  <th class="w-50px text-center">{{ $t('label.action') }}</th>
                </tr>
                </thead>
            </table>
          </template>
        </Card>
      </template>
    </SubLayout>
</template>

<script setup>

import {computed, nextTick, onMounted, onUpdated, ref, watch} from "vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {formattedLocalAmount} from "@/mixins/Helpers";
import {useRouter} from "vue-router";
import {useLocales} from "@/composables/locales";
import {useHasAbility} from "@/composables/hasAbility";
import Card from "@/Modules/CRM/components/Card";
import Button from "@/components/Button.vue";
import ActionsBar from "@/Modules/CRM/views/Payment/components/ActionsBar.vue";
import abilities from "@/composables/abilities";
import {Theme} from "@/composables/theming";

const router = useRouter()
const store = useStore()
const { can } = useHasAbility()
const {locale, datatableLocaleUrl} = useLocales()

const title = ref("breadcrumbs.payment.title")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.payment.list',
    route: 'payments'
  }
])

const table = ref()
const query = ref({
  status: 'all',
  contact: 'all',
  start_date: '',
  end_date: ''
})

const paymentStatus = computed(() => store.getters['crmPayments/paymentStatus'])
const contacts = computed(() => store.getters['contacts/getContacts'])

watch(() => locale.value, (value) => {
  nextTick(() => fetchDatas() )
}, { deep: true })

watch(() => query.value.status, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/payments/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

watch(() => query.value.contact, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/payments/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

watch(() => [query.value.start_date, query.value.end_date], (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/payments/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#payments_datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'desc']],
    ajax: {
      url: BASE_URL + `/get/payments/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
      { data: "date" }, { data: "payment_number" }, { data: "contact" }, { data: "payment_type" }, { data: "invoice_number" }, { data: "status" }, { data: "amount" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [
      {
        targets: -8,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -7,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -6,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -3,
        orderable: false,
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          let status = getPaymentStatusName(t)
          const locale = i18n.global.locale.value
          let color = status.color
          status = status[locale]
          return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
        },
      },
      {
        targets: -2,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + formattedLocalAmount(t) + ' ' + e.currency + "</span>";
        },
      },

      {
        width: "15%",
        targets: -1,
        "className": "dt-center text-center",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          if(can(abilities.VIEW_PAYMENT) || can(abilities.VIEW_ALL_PAYMENT)){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
          }
          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("click", "#payments_datatable .show_btn", (e) => {
    router.push({ name: 'payments-view', params: { id: e.target.getAttribute('data-id') } })
  });

  $(document).on("click", "#payments_datatable .edit_btn", (e) => {
    router.push({ name: 'payments-edit', params: { id: e.target.getAttribute('data-id') } })
  });
}

function handleRangeDate (range) {
  query.value.start_date = range.start_date
  query.value.end_date = range.end_date
}

function getPaymentStatusName(status) {
  const {color, fr, en} = paymentStatus.value.find(el => el.value === status) || ''
  return {color, fr, en}
}

function openCreateView(){
  router.push({ name: 'payments-create' })
}

function loadData() {
  Promise.all([
    store.dispatch('crmPayments/loadPaymentStatus'),
    store.dispatch('contacts/loadContacts'),
    fetchDatas()
  ])
}

onMounted( () => {
  loadData()
})


</script>